/**
 * Slot-Parser für Mandatsannahme / Erstberatung.
 * Satz → Name / Gegenstand / Dringlichkeit — nicht 1:1 als Mandantenname.
 */

export const MANDATS_DRING_LABELS = {
  niedrig: 'Niedrig — Routine',
  mittel: 'Mittel — zeitnah',
  hoch: 'Hoch — dringend',
} as const;

export type MandatsDringLabel =
  (typeof MANDATS_DRING_LABELS)[keyof typeof MANDATS_DRING_LABELS];

export interface MandatsannahmeSlots {
  mandant?: string;
  gegenstand?: string;
  dringlichkeit?: MandatsDringLabel | string;
}

const SENTENCE_MARKERS =
  /\b(lautet|heißt|es geht um|gegenstand|anliegen|sachverhalt|dringlich|mandant(?:in)?|firma\s+(?:heißt|ist)|name(?:n)?\s+(?:lautet|ist))\b/i;

const NAME_LEAK =
  /\b(lautet|heißt|gegenstand|anliegen|dringlich|es geht um|sachverhalt|mandant(?:in)?)\b/i;

const NAME_PATTERNS: RegExp[] = [
  /(?:der\s+)?name(?:n)?\s+(?:lautet|ist)\s*:?\s*(.+?)(?=[,.;]|\s+(?:gegenstand|anliegen|es geht|dring|sachverhalt)|$)/i,
  /(?:der\s+)?mandant(?:in)?(?:enname)?\s+(?:heißt|ist|lautet)\s*:?\s*(.+?)(?=[,.;]|\s+(?:gegenstand|anliegen|es geht|dring|sachverhalt)|$)/i,
  /(?:die\s+)?firma\s+(?:heißt|ist|lautet)\s*:?\s*(.+?)(?=[,.;]|\s+(?:gegenstand|anliegen|es geht|dring|sachverhalt)|$)/i,
  /mandant(?:enname)?\s*:\s*(.+?)(?=[,.;]|$)/i,
  /heißt\s+(.+?)(?=[,.;]|\s+(?:gegenstand|anliegen|es geht|dring|sachverhalt)|$)/i,
];

const SUBJECT_PATTERNS: RegExp[] = [
  /(?:gegenstand|anliegen|sachverhalt)\s*(?:ist|:)\s*(.+?)(?=[,.;]|\s+dring|$)/i,
  /(?:es geht um|betrifft)\s+(.+?)(?=[,.;]|\s+dring|$)/i,
  /\bwegen\s+(.+?)(?=[,.;]|\s+dring|$)/i,
];

export function isMandatsSentenceLike(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (t.length > 72) return true;
  if (SENTENCE_MARKERS.test(t)) return true;
  const words = t.split(/\s+/).filter(Boolean);
  if (/,|;/.test(t) && words.length > 5) return true;
  return false;
}

export function parseDringlichkeitSlot(text: string): MandatsDringLabel | undefined {
  const t = text.trim();
  if (!t) return undefined;

  if (/hoch\s*[—–-]\s*dringend/i.test(t) || /^hoch$/i.test(t)) {
    return MANDATS_DRING_LABELS.hoch;
  }
  if (/mittel\s*[—–-]\s*zeitnah/i.test(t) || /^mittel$/i.test(t)) {
    return MANDATS_DRING_LABELS.mittel;
  }
  if (/niedrig\s*[—–-]\s*routine/i.test(t) || /^niedrig$/i.test(t)) {
    return MANDATS_DRING_LABELS.niedrig;
  }

  const hasHigh = /\b(hoch|dringend|eilig|sofort|akut)\b/i.test(t);
  const hasLow = /\b(niedrig|routine|keine?\s+eile)\b/i.test(t);
  const hasMid = /\b(mittel|zeitnah|bald)\b/i.test(t);

  if (hasHigh && !hasLow) return MANDATS_DRING_LABELS.hoch;
  if (hasLow && !hasHigh) return MANDATS_DRING_LABELS.niedrig;
  if (hasMid && !hasHigh) return MANDATS_DRING_LABELS.mittel;
  return undefined;
}

function cleanCapture(raw: string): string {
  let s = raw.replace(/["„“']/g, ' ').replace(/\s+/g, ' ').trim();
  s = s.replace(/^(der|die|das|ein|eine|den|dem|einen)\s+/i, '');
  s = s.replace(/\s+(und|mit|zu)$/i, '');
  s = s.replace(/[.,;:]+$/g, '').trim();
  if (s.length > 80) s = s.slice(0, 80).trim();
  return s;
}

function cleanSubject(raw: string): string {
  let s = cleanCapture(raw);
  s = s.replace(/\s*,?\s*(?:dringlichkeit\s*(?:ist|:))?\s*(?:hoch|mittel|niedrig|dringend|eilig|zeitnah|routine).*$/i, '');
  s = s.replace(/^(einen?|eine)\s+/i, '').trim();
  return s.slice(0, 120);
}

function isChipOnlyDringlichkeit(text: string): boolean {
  return /^(niedrig|mittel|hoch)(\s*[—–-]\s+\S.*)?$/i.test(text.trim());
}

function tryCommaSlots(text: string): MandatsannahmeSlots | null {
  const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2 || parts.length > 4) return null;

  const result: MandatsannahmeSlots = {};
  const first = parts[0];
  const fromPattern = extractName(first);
  if (fromPattern) {
    result.mandant = fromPattern;
  } else if (!isMandatsSentenceLike(first) || first.split(/\s+/).length <= 6) {
    const name = cleanCapture(first);
    if (name && !NAME_LEAK.test(name)) result.mandant = name;
  }
  if (!result.mandant) return null;

  for (const part of parts.slice(1)) {
    const d = parseDringlichkeitSlot(part);
    if (d && !result.dringlichkeit) {
      result.dringlichkeit = d;
      continue;
    }
    const sub = extractSubject(part) || cleanSubject(part);
    if (sub && !result.gegenstand) result.gegenstand = sub;
    else if (sub && result.gegenstand) result.gegenstand = `${result.gegenstand}, ${sub}`;
  }
  return result;
}

function extractName(text: string): string | undefined {
  for (const re of NAME_PATTERNS) {
    const m = text.match(re);
    if (m?.[1]) {
      const name = cleanCapture(m[1]);
      if (name && !NAME_LEAK.test(name)) return name;
    }
  }
  return undefined;
}

function extractSubject(text: string): string | undefined {
  for (const re of SUBJECT_PATTERNS) {
    const m = text.match(re);
    if (m?.[1]) {
      const sub = cleanSubject(m[1]);
      if (sub) return sub;
    }
  }
  return undefined;
}

/** Kontextfrei: Satz oder Kurzslot in Felder zerlegen. */
export function parseMandatsannahmeSlots(text: string): MandatsannahmeSlots {
  const t = String(text ?? '').trim();
  if (!t) return {};

  if (isChipOnlyDringlichkeit(t)) {
    const d = parseDringlichkeitSlot(t);
    return d ? { dringlichkeit: d } : {};
  }

  const comma = tryCommaSlots(t);
  if (comma?.mandant) {
    const d = parseDringlichkeitSlot(t);
    if (d && !comma.dringlichkeit) comma.dringlichkeit = d;
    if (!comma.gegenstand) {
      const sub = extractSubject(t);
      if (sub) comma.gegenstand = sub;
    }
    return comma;
  }

  const result: MandatsannahmeSlots = {};
  const d = parseDringlichkeitSlot(t);
  if (d) result.dringlichkeit = d;

  const name = extractName(t);
  if (name) result.mandant = name;

  const sub = extractSubject(t);
  if (sub) result.gegenstand = sub;

  if (!result.mandant && !isMandatsSentenceLike(t)) {
    result.mandant = t.slice(0, 80);
  }

  return result;
}

export function sanitizeMandantLabel(raw: string): string {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  const parsed = parseMandatsannahmeSlots(t);
  if (parsed.mandant && !NAME_LEAK.test(parsed.mandant)) {
    return parsed.mandant.slice(0, 80);
  }
  if (!isMandatsSentenceLike(t) && !NAME_LEAK.test(t) && t.length <= 80) {
    return t;
  }
  const stripped = t
    .replace(/(?:der\s+)?name(?:n)?\s+(?:lautet|ist)\s*:?\s*/i, '')
    .replace(/(?:der\s+)?mandant(?:in)?\s+(?:heißt|ist|lautet)\s*:?\s*/i, '')
    .split(/[,.;]/)[0]
    .trim();
  if (stripped && !NAME_LEAK.test(stripped) && stripped.length <= 80) {
    return stripped;
  }
  return 'Mandant';
}

export function buildMandatsAkteTitle(slots: MandatsannahmeSlots): string {
  const name = sanitizeMandantLabel(slots.mandant ?? '') || 'Mandant';
  const subject = (slots.gegenstand ?? '').replace(/\s+/g, ' ').trim().slice(0, 40);
  const title = subject ? `Beratung — ${name} — ${subject}` : `Beratung — ${name}`;
  return title.slice(0, 120);
}

/**
 * Confirm-Step: Anwalt korrigiert die drei Slots direkt (kein Satz-Parser).
 * Leere Felder lassen den bisherigen Wert stehen.
 */
export function applyMandatsConfirmEdits(
  data: MandatsannahmeSlots,
  edits: MandatsannahmeSlots,
): MandatsannahmeSlots {
  const next: MandatsannahmeSlots = { ...data };

  const mandantRaw = (edits.mandant ?? '').replace(/\s+/g, ' ').trim();
  if (mandantRaw) {
    next.mandant =
      NAME_LEAK.test(mandantRaw) || isMandatsSentenceLike(mandantRaw)
        ? sanitizeMandantLabel(mandantRaw) || mandantRaw.slice(0, 80)
        : mandantRaw.slice(0, 80);
  }

  const gegenstandRaw = (edits.gegenstand ?? '').replace(/\s+/g, ' ').trim();
  if (gegenstandRaw) {
    next.gegenstand = gegenstandRaw.slice(0, 120);
  }

  const dringRaw = (edits.dringlichkeit ?? '').trim();
  if (dringRaw) {
    next.dringlichkeit = parseDringlichkeitSlot(dringRaw) || dringRaw.slice(0, 40);
  }

  return next;
}

/** Step-bewusst: Kurzslot „Kündigung“ bleibt Gegenstand, nicht Mandantenname. */
export function mergeMandatsSlots(
  step: 'mandant' | 'gegenstand' | 'dringlichkeit',
  text: string,
  data: MandatsannahmeSlots,
): MandatsannahmeSlots {
  const slots = parseMandatsannahmeSlots(text);
  const next: MandatsannahmeSlots = { ...data };

  if (step === 'mandant') {
    next.mandant = slots.mandant || sanitizeMandantLabel(text);
    if (slots.gegenstand) next.gegenstand = slots.gegenstand;
    if (slots.dringlichkeit) next.dringlichkeit = slots.dringlichkeit;
  } else if (step === 'gegenstand') {
    next.gegenstand = slots.gegenstand || text.trim();
    if (slots.dringlichkeit) next.dringlichkeit = slots.dringlichkeit;
    if (!next.mandant && slots.mandant && isMandatsSentenceLike(text)) {
      next.mandant = slots.mandant;
    }
  } else {
    next.dringlichkeit = parseDringlichkeitSlot(text) || text.trim();
  }

  return next;
}

export function mandatsNextStep(
  data: MandatsannahmeSlots,
): 'gegenstand' | 'dringlichkeit' | 'upload_optional' {
  if (!data.mandant?.trim()) return 'gegenstand';
  if (!data.gegenstand?.trim()) return 'gegenstand';
  if (!data.dringlichkeit?.trim()) return 'dringlichkeit';
  return 'upload_optional';
}
